import {
  useCreate,
  useDelete,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { ConfirmDeleteButton, FormActions } from "@/components/competitors/ui";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import {
  formatLongDate,
  isIsoDay,
  toIsoDay,
  weekStartOf,
} from "@/components/market-intel/dates";
import { Input } from "@/components/ui/input";

import { useMarketing } from "./context";
import {
  metricsFormDefaults,
  normalizeMetricsForm,
  type MetricsFormValues,
  type MetricsTarget,
} from "./forms";
import type { ContentMetric, MetricField } from "./types";

const GROUPS: { key: string; fields: MetricField[] }[] = [
  { key: "key", fields: ["saves", "shares", "direct_messages"] },
  { key: "reach", fields: ["impressions", "reach", "views"] },
  { key: "interactions", fields: ["likes", "comments", "clicks", "follows"] },
  { key: "results", fields: ["leads", "spend"] },
];

const targetValue = (target: MetricsTarget | undefined): string =>
  target ? `${target.kind}:${target.id}` : "";

const parseTarget = (value: string): MetricsTarget | undefined => {
  const [kind, id] = value.split(":");
  if (!id || (kind !== "piece" && kind !== "channel")) return undefined;
  return { kind, id: /^\d+$/.test(id) ? Number(id) : id };
};

const sameTarget = (row: ContentMetric, target: MetricsTarget): boolean =>
  target.kind === "piece"
    ? String(row.content_item_id) === String(target.id)
    : row.content_item_id == null &&
      String(row.channel_id) === String(target.id);

/**
 * "Registrar métricas": one week of a piece or of a channel. Saving a week
 * that already has a row updates it (same as the agents' upsert).
 */
export const MetricsForm = ({
  open,
  onOpenChange,
  target: initialTarget,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target?: MetricsTarget;
  /** Edits this row. */
  row?: ContentMetric | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const formId = useId();
  const { data } = useMarketing();
  const today = toIsoDay(new Date());
  const [target, setTarget] = useState(targetValue(initialTarget));
  const [values, setValues] = useState(() =>
    metricsFormDefaults(today, row, locale),
  );
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();
  const [remove, { isPending: deleting }] = useDelete();

  useEffect(() => {
    if (open) {
      setValues(metricsFormDefaults(today, row, locale));
      setTarget(targetValue(initialTarget));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row, initialTarget]);

  const set = (key: keyof MetricsFormValues) => (value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
  };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseTarget(target);
    if (!parsed) return setError("marketing.form.week_start_required");
    const result = normalizeMetricsForm(values, parsed);
    if (!result.ok) return setError(result.error);
    const existing =
      row ??
      data.metrics.find(
        (entry) =>
          sameTarget(entry, parsed) &&
          entry.week_start === result.data.week_start,
      );
    const options = {
      onSuccess: () => {
        notify("marketing.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("marketing.form.error", { type: "error" }),
    };
    if (existing)
      update(
        "content_metrics",
        {
          id: existing.id,
          data: { ...result.data, source: existing.source ?? "manual" },
          previousData: existing,
        },
        options,
      );
    else create("content_metrics", { data: result.data }, options);
  };

  const pieces = [...data.items]
    .filter((item) => item.status === "publicado")
    .sort((a, b) =>
      (b.published_at ?? b.scheduled_at ?? "").localeCompare(
        a.published_at ?? a.scheduled_at ?? "",
      ),
    );
  const monday = isIsoDay(values.week_start)
    ? weekStartOf(values.week_start)
    : null;
  const errorId = `${formId}-error`;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        row ? "marketing.metrics_form.edit" : "marketing.metrics_form.title",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <>
          {row ? (
            <ConfirmDeleteButton
              label={translate("marketing.piece.delete")}
              disabled={deleting}
              className="mr-auto"
              onConfirm={() =>
                remove(
                  "content_metrics",
                  { id: row.id, previousData: row },
                  { onSuccess: close },
                )
              }
            />
          ) : null}
          <FormActions
            formId={formId}
            disabled={creating || updating}
            onCancel={close}
          />
        </>
      }
    >
      <Field
        id={`${formId}-target`}
        label={translate("marketing.metrics_form.target")}
      >
        <select
          id={`${formId}-target`}
          className={SELECT_CLASS}
          value={target}
          disabled={!!row}
          onChange={(event) => {
            setTarget(event.target.value);
            setError(null);
          }}
        >
          <option value="" disabled>
            —
          </option>
          <optgroup label={translate("marketing.metrics_form.target_piece")}>
            {pieces.map((item) => (
              <option key={item.id} value={`piece:${item.id}`}>
                {item.title}
              </option>
            ))}
          </optgroup>
          <optgroup label={translate("marketing.metrics_form.target_channel")}>
            {data.channels.map((channel) => (
              <option key={channel.id} value={`channel:${channel.id}`}>
                {channel.name}
              </option>
            ))}
          </optgroup>
        </select>
      </Field>
      <Field
        id={`${formId}-week`}
        label={translate("marketing.metrics_form.week")}
      >
        <DateField
          id={`${formId}-week`}
          value={values.week_start}
          onChange={(event) => set("week_start")(event.target.value)}
        />
        {monday && monday !== values.week_start ? (
          <p className="text-xs text-muted-foreground">
            {translate("marketing.metrics_form.week_hint", {
              day: formatLongDate(monday, locale),
            })}
          </p>
        ) : null}
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      {GROUPS.map((group) => (
        <fieldset key={group.key} className="space-y-2">
          <legend className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {translate(`marketing.metrics_form.${group.key}`)}
          </legend>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {group.fields.map((name) => (
              <Field
                key={name}
                id={`${formId}-${name}`}
                label={translate(`marketing.metric.${name}`)}
              >
                <Input
                  id={`${formId}-${name}`}
                  inputMode={name === "spend" ? "decimal" : "numeric"}
                  value={values[name]}
                  onChange={(event) => set(name)(event.target.value)}
                  aria-describedby={error ? errorId : undefined}
                />
              </Field>
            ))}
          </div>
        </fieldset>
      ))}
      <Field
        id={`${formId}-notes`}
        label={translate("marketing.metrics_form.notes")}
      >
        <Input
          id={`${formId}-notes`}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
};
